import type { PropFirm, PropFirmPlan, PropFirmAccount } from '../types';

/** SMOG eligible if ANY plan has fundedConsistencyRulePct >= 50% or null (no rule) */
export function isFirmSMOGEligible(firm: PropFirm): boolean {
  return firm.plans.some(
    (plan) => plan.fundedConsistencyRulePct === null || plan.fundedConsistencyRulePct >= 0.50,
  );
}

// ─── FUTURES FIRMS ──────────────────────────────────────────────────────────

const apex: PropFirm = {
  id: 'apex',
  name: 'Apex Trader Funding',
  market: 'futures',
  maxEvalAccounts: null, // unlimited
  maxFundedAccounts: 20,
  plans: [
    {
      id: 'intraday-trail',
      name: 'Intraday Trail',
      hasEval: true,
      drawdownType: 'trailing',
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.50,
      payoutSplit: 1.00,
      payoutFrequency: 'weekly',
      evalDays: 30,
      accounts: [
        { size: 25000, profitTarget: 1500, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 4, fundedMaxContracts: 2, fundedDailyLossLimit: 500, activationFee: 79 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: null, maxContracts: 6, fundedMaxContracts: 4, fundedDailyLossLimit: 1000, activationFee: 79 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: null, maxContracts: 8, fundedMaxContracts: 6, fundedDailyLossLimit: 1500, activationFee: 79 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4000, dailyLossLimit: null, maxContracts: 12, fundedMaxContracts: 10, fundedDailyLossLimit: 2000, activationFee: 79 },
      ],
    },
    {
      id: 'eod-trail',
      name: 'EOD Trail',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.50,
      payoutSplit: 1.00,
      payoutFrequency: 'weekly',
      evalDays: 30,
      accounts: [
        { size: 25000, profitTarget: 1500, maxDrawdown: 1000, dailyLossLimit: 500, maxContracts: 4, fundedMaxContracts: 2, activationFee: 99 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: 1000, maxContracts: 6, fundedMaxContracts: 4, activationFee: 99 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: 1500, maxContracts: 8, fundedMaxContracts: 6, activationFee: 99 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4000, dailyLossLimit: 2000, maxContracts: 12, fundedMaxContracts: 10, activationFee: 99 },
      ],
    },
  ],
};

const topstep: PropFirm = {
  id: 'topstep',
  name: 'Topstep',
  market: 'futures',
  maxEvalAccounts: null, // unlimited (max 10 new purchases/month)
  maxFundedAccounts: 5,
  plans: [
    {
      id: 'standard',
      name: 'Standard',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: 0.50,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.90,
      payoutFrequency: 'weekly',
      evalDays: null, // unlimited (subscription-based)
      accounts: [
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: 1000, maxContracts: 5, activationFee: 149 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: 2000, maxContracts: 10, activationFee: 149 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4500, dailyLossLimit: 3000, maxContracts: 15, activationFee: 149 },
      ],
    },
    {
      id: 'consistency',
      name: 'Consistency',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: 0.50,
      fundedConsistencyRulePct: 0.40,
      payoutSplit: 0.90,
      payoutFrequency: 'weekly',
      evalDays: null,
      accounts: [
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: 1000, maxContracts: 5, activationFee: 149 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: 2000, maxContracts: 10, activationFee: 149 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4500, dailyLossLimit: 3000, maxContracts: 15, activationFee: 149 },
      ],
    },
  ],
};

const lucid: PropFirm = {
  id: 'lucid',
  name: 'Lucid Trading',
  market: 'futures',
  maxEvalAccounts: 10,
  maxFundedAccounts: 5,
  plans: [
    {
      id: 'pro',
      name: 'LucidPro',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.40,
      payoutSplit: 0.90,
      payoutFrequency: 'on-demand',
      evalDays: null, // unlimited
      accounts: [
        { size: 25000, profitTarget: 1250, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 2, activationFee: 0 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: 1200, maxContracts: 4, activationFee: 0 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: 1800, maxContracts: 6, activationFee: 0 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4500, dailyLossLimit: 2700, maxContracts: 10, activationFee: 0 },
      ],
    },
    {
      id: 'flex',
      name: 'LucidFlex',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: 0.50,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.90,
      payoutFrequency: 'on-demand',
      evalDays: null,
      accounts: [
        { size: 25000, profitTarget: 1250, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 2, activationFee: 0 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: null, maxContracts: 4, activationFee: 0 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: null, maxContracts: 6, activationFee: 0 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4500, dailyLossLimit: null, maxContracts: 10, activationFee: 0 },
      ],
    },
    {
      id: 'direct',
      name: 'LucidDirect',
      hasEval: false,
      drawdownType: 'eod',
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.20,
      payoutSplit: 0.90,
      payoutFrequency: 'on-demand',
      evalDays: null,
      accounts: [
        { size: 25000, profitTarget: 0, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 2, activationFee: 0 },
        { size: 50000, profitTarget: 0, maxDrawdown: 2000, dailyLossLimit: 1200, maxContracts: 4, activationFee: 0 },
        { size: 100000, profitTarget: 0, maxDrawdown: 3500, dailyLossLimit: 2100, maxContracts: 6, activationFee: 0 },
        { size: 150000, profitTarget: 0, maxDrawdown: 5000, dailyLossLimit: 3000, maxContracts: 10, activationFee: 0 },
      ],
    },
  ],
};

const tradeify: PropFirm = {
  id: 'tradeify',
  name: 'Tradeify',
  market: 'futures',
  maxEvalAccounts: 5,
  maxFundedAccounts: 5,
  plans: [
    {
      id: 'growth',
      name: 'Growth',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.35,
      payoutSplit: 0.90, // first $15K at 100%, then 90/10
      payoutFrequency: '5-day',
      evalDays: null, // unlimited
      accounts: [
        { size: 25000, profitTarget: 1500, maxDrawdown: 1000, dailyLossLimit: 600, maxContracts: 1, activationFee: 0 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: 1250, maxContracts: 4, activationFee: 0 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3500, dailyLossLimit: 2500, maxContracts: 8, activationFee: 0 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 5000, dailyLossLimit: 3750, maxContracts: 12, activationFee: 0 },
      ],
    },
    {
      id: 'select',
      name: 'Select',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: 0.40,
      fundedConsistencyRulePct: null, // no funded consistency on Select
      payoutSplit: 0.90,
      payoutFrequency: '5-day',
      evalDays: null,
      accounts: [
        { size: 25000, profitTarget: 1500, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 1, activationFee: 0 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: null, maxContracts: 4, activationFee: 0 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: null, maxContracts: 8, activationFee: 0 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4500, dailyLossLimit: null, maxContracts: 12, activationFee: 0 },
      ],
    },
    {
      id: 'lightning',
      name: 'Lightning',
      hasEval: false,
      drawdownType: 'eod',
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.20, // progressive: 20%/25%/30% — modeled as initial 20%
      payoutSplit: 0.90,
      payoutFrequency: '7-day',
      evalDays: null,
      accounts: [
        { size: 25000, profitTarget: 0, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 1, activationFee: 0 },
        { size: 50000, profitTarget: 0, maxDrawdown: 2000, dailyLossLimit: 1250, maxContracts: 4, activationFee: 0 },
        { size: 100000, profitTarget: 0, maxDrawdown: 4000, dailyLossLimit: 2500, maxContracts: 8, activationFee: 0 },
        { size: 150000, profitTarget: 0, maxDrawdown: 5250, dailyLossLimit: 3000, maxContracts: 12, activationFee: 0 },
      ],
    },
  ],
};

const mff: PropFirm = {
  id: 'mff',
  name: 'My Funded Futures',
  market: 'futures',
  maxEvalAccounts: 10,
  maxFundedAccounts: 5, // up to 5 if all 25K/50K; max 3 if any 100K/150K
  plans: [
    {
      id: 'pro',
      name: 'Pro',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: 0.50,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: 'bi-weekly',
      evalDays: null, // unlimited (min 2 trading days)
      accounts: [
        { size: 25000, profitTarget: 1500, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 3, activationFee: 0 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: null, maxContracts: 6, activationFee: 0 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: null, maxContracts: 9, activationFee: 0 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4500, dailyLossLimit: null, maxContracts: 14, activationFee: 0 },
      ],
    },
    {
      id: 'rapid',
      name: 'Rapid',
      hasEval: true,
      drawdownType: 'eod',
      fundedDrawdownType: 'trailing', // funded is intraday trailing
      consistencyRulePct: 0.50,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.90,
      payoutFrequency: 'daily',
      evalDays: null,
      accounts: [
        { size: 25000, profitTarget: 1500, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 3, activationFee: 0 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: null, maxContracts: 5, activationFee: 0 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: null, maxContracts: 10, activationFee: 0 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 4500, dailyLossLimit: null, maxContracts: 15, activationFee: 0 },
      ],
    },
    {
      id: 'flex',
      name: 'Flex',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: 0.50,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: '5-day',
      evalDays: null,
      accounts: [
        { size: 25000, profitTarget: 1500, maxDrawdown: 1000, dailyLossLimit: null, maxContracts: 3, activationFee: 0 },
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: null, maxContracts: 5, activationFee: 0 },
      ],
    },
  ],
};

// ─── CRYPTO FIRMS ───────────────────────────────────────────────────────────

const tradeifyCrypto: PropFirm = {
  id: 'tradeify-crypto',
  name: 'Tradeify Crypto',
  market: 'crypto',
  maxEvalAccounts: 5,
  maxFundedAccounts: 3,
  plans: [
    {
      id: '1-step',
      name: '1-Step',
      hasEval: true,
      drawdownType: 'static', // eval is static 6%
      fundedDrawdownType: 'eod', // funded is EOD trailing
      consistencyRulePct: null,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: 'on-demand',
      evalDays: null, // unlimited
      accounts: [
        { size: 5000, profitTarget: 600, maxDrawdown: 300, dailyLossLimit: 150, activationFee: 0 },
        { size: 10000, profitTarget: 1200, maxDrawdown: 600, dailyLossLimit: 300, activationFee: 0 },
        { size: 25000, profitTarget: 3000, maxDrawdown: 1500, dailyLossLimit: 750, activationFee: 0 },
        { size: 50000, profitTarget: 6000, maxDrawdown: 3000, dailyLossLimit: 1500, activationFee: 0 },
        { size: 100000, profitTarget: 12000, maxDrawdown: 6000, dailyLossLimit: 3000, activationFee: 0 },
      ],
    },
    {
      id: '2-step',
      name: '2-Step',
      hasEval: true,
      drawdownType: 'static', // eval is static 6%
      fundedDrawdownType: 'eod', // funded is EOD trailing
      consistencyRulePct: null,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: 'on-demand',
      evalDays: null,
      accounts: [
        { size: 5000, profitTarget: 500, maxDrawdown: 300, dailyLossLimit: 150, activationFee: 0 },
        { size: 10000, profitTarget: 1000, maxDrawdown: 600, dailyLossLimit: 300, activationFee: 0 },
        { size: 25000, profitTarget: 2500, maxDrawdown: 1500, dailyLossLimit: 750, activationFee: 0 },
        { size: 50000, profitTarget: 5000, maxDrawdown: 3000, dailyLossLimit: 1500, activationFee: 0 },
        { size: 100000, profitTarget: 10000, maxDrawdown: 6000, dailyLossLimit: 3000, activationFee: 0 },
      ],
    },
    {
      id: 'instant',
      name: 'Instant',
      hasEval: false,
      drawdownType: 'trailing', // trailing from day one
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.20,
      payoutSplit: 0.80,
      payoutFrequency: 'on-demand',
      evalDays: null,
      accounts: [
        { size: 5000, profitTarget: 0, maxDrawdown: 300, dailyLossLimit: 150, activationFee: 0 },
        { size: 10000, profitTarget: 0, maxDrawdown: 600, dailyLossLimit: 300, activationFee: 0 },
        { size: 25000, profitTarget: 0, maxDrawdown: 1500, dailyLossLimit: 750, activationFee: 0 },
        { size: 50000, profitTarget: 0, maxDrawdown: 3000, dailyLossLimit: 1500, activationFee: 0 },
        { size: 100000, profitTarget: 0, maxDrawdown: 6000, dailyLossLimit: 3000, activationFee: 0 },
      ],
    },
  ],
};

const breakoutProp: PropFirm = {
  id: 'breakout',
  name: 'Breakout Prop',
  market: 'crypto',
  maxEvalAccounts: null,
  maxFundedAccounts: null, // $200K max funded allocation
  plans: [
    {
      id: '1-step',
      name: 'Classic 1-Step',
      hasEval: true,
      drawdownType: 'static',
      consistencyRulePct: null,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: 'on-demand',
      evalDays: null, // unlimited
      accounts: [
        { size: 5000, profitTarget: 500, maxDrawdown: 300, dailyLossLimit: 150, activationFee: 0 },
        { size: 10000, profitTarget: 1000, maxDrawdown: 600, dailyLossLimit: 300, activationFee: 0 },
        { size: 25000, profitTarget: 2500, maxDrawdown: 1500, dailyLossLimit: 750, activationFee: 0 },
        { size: 50000, profitTarget: 5000, maxDrawdown: 3000, dailyLossLimit: 1500, activationFee: 0 },
        { size: 100000, profitTarget: 10000, maxDrawdown: 6000, dailyLossLimit: 3000, activationFee: 0 },
        { size: 200000, profitTarget: 20000, maxDrawdown: 12000, dailyLossLimit: 6000, activationFee: 0 },
      ],
    },
    {
      id: '2-step',
      name: 'Classic 2-Step',
      hasEval: true,
      drawdownType: 'trailing',
      consistencyRulePct: null,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: 'on-demand',
      evalDays: null,
      accounts: [
        { size: 5000, profitTarget: 250, maxDrawdown: 400, dailyLossLimit: 250, activationFee: 0 },
        { size: 10000, profitTarget: 500, maxDrawdown: 800, dailyLossLimit: 500, activationFee: 0 },
        { size: 25000, profitTarget: 1250, maxDrawdown: 2000, dailyLossLimit: 1250, activationFee: 0 },
        { size: 50000, profitTarget: 2500, maxDrawdown: 4000, dailyLossLimit: 2500, activationFee: 0 },
        { size: 100000, profitTarget: 5000, maxDrawdown: 8000, dailyLossLimit: 5000, activationFee: 0 },
      ],
    },
  ],
};

const warbux: PropFirm = {
  id: 'warbux',
  name: 'Warbux',
  market: 'crypto',
  maxEvalAccounts: null,
  maxFundedAccounts: null,
  plans: [
    {
      id: '1-step',
      name: '1-Step',
      hasEval: true,
      drawdownType: 'static',
      consistencyRulePct: 0.33,
      fundedConsistencyRulePct: 0.33,
      payoutSplit: 0.80,
      payoutFrequency: 'daily',
      evalDays: null, // unlimited
      accounts: [
        { size: 5000, profitTarget: 500, maxDrawdown: 300, dailyLossLimit: 200, activationFee: 0 },
        { size: 10000, profitTarget: 1000, maxDrawdown: 600, dailyLossLimit: 400, activationFee: 0 },
        { size: 25000, profitTarget: 2500, maxDrawdown: 1500, dailyLossLimit: 1000, activationFee: 0 },
        { size: 50000, profitTarget: 5000, maxDrawdown: 3000, dailyLossLimit: 2000, activationFee: 0 },
        { size: 100000, profitTarget: 10000, maxDrawdown: 6000, dailyLossLimit: 4000, activationFee: 0 },
      ],
    },
    {
      id: 'instant',
      name: 'Instant',
      hasEval: false,
      drawdownType: 'static',
      consistencyRulePct: null,
      fundedConsistencyRulePct: 0.20,
      payoutSplit: 0.80,
      payoutFrequency: 'daily',
      evalDays: null,
      accounts: [
        { size: 5000, profitTarget: 0, maxDrawdown: 300, dailyLossLimit: 150, activationFee: 0 },
        { size: 10000, profitTarget: 0, maxDrawdown: 600, dailyLossLimit: 300, activationFee: 0 },
        { size: 25000, profitTarget: 0, maxDrawdown: 1500, dailyLossLimit: 750, activationFee: 0 },
        { size: 50000, profitTarget: 0, maxDrawdown: 3000, dailyLossLimit: 1500, activationFee: 0 },
        { size: 100000, profitTarget: 0, maxDrawdown: 6000, dailyLossLimit: 3000, activationFee: 0 },
      ],
    },
  ],
};

const cryptoFundTrader: PropFirm = {
  id: 'cft',
  name: 'Crypto Fund Trader',
  market: 'crypto',
  maxEvalAccounts: null,
  maxFundedAccounts: null,
  plans: [
    {
      id: '2-phase',
      name: '2-Phase',
      hasEval: true,
      drawdownType: 'static',
      consistencyRulePct: null,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: 'bi-weekly',
      evalDays: null, // unlimited
      accounts: [
        { size: 5000, profitTarget: 400, maxDrawdown: 500, dailyLossLimit: 250, activationFee: 0 },
        { size: 10000, profitTarget: 800, maxDrawdown: 1000, dailyLossLimit: 500, activationFee: 0 },
        { size: 25000, profitTarget: 2000, maxDrawdown: 2500, dailyLossLimit: 1250, activationFee: 0 },
        { size: 50000, profitTarget: 4000, maxDrawdown: 5000, dailyLossLimit: 2500, activationFee: 0 },
        { size: 100000, profitTarget: 8000, maxDrawdown: 10000, dailyLossLimit: 5000, activationFee: 0 },
        { size: 200000, profitTarget: 16000, maxDrawdown: 20000, dailyLossLimit: 10000, activationFee: 0 },
      ],
    },
    {
      id: '1-phase',
      name: '1-Phase',
      hasEval: true,
      drawdownType: 'trailing',
      consistencyRulePct: null,
      fundedConsistencyRulePct: null,
      payoutSplit: 0.80,
      payoutFrequency: 'bi-weekly',
      evalDays: null,
      accounts: [
        { size: 5000, profitTarget: 500, maxDrawdown: 300, dailyLossLimit: 200, activationFee: 0 },
        { size: 10000, profitTarget: 1000, maxDrawdown: 600, dailyLossLimit: 400, activationFee: 0 },
        { size: 25000, profitTarget: 2500, maxDrawdown: 1500, dailyLossLimit: 1000, activationFee: 0 },
        { size: 50000, profitTarget: 5000, maxDrawdown: 3000, dailyLossLimit: 2000, activationFee: 0 },
        { size: 100000, profitTarget: 10000, maxDrawdown: 6000, dailyLossLimit: 4000, activationFee: 0 },
        { size: 200000, profitTarget: 20000, maxDrawdown: 12000, dailyLossLimit: 8000, activationFee: 0 },
      ],
    },
  ],
};

const e8Markets: PropFirm = {
  id: 'e8',
  name: 'E8 Markets',
  market: 'crypto',
  maxEvalAccounts: null,
  maxFundedAccounts: null,
  plans: [
    {
      id: 'signature',
      name: 'Signature',
      hasEval: true,
      drawdownType: 'eod',
      consistencyRulePct: 0.35,
      fundedConsistencyRulePct: 0.35,
      payoutSplit: 0.80,
      payoutFrequency: 'bi-weekly',
      evalDays: null, // unlimited (60-day inactivity rule)
      accounts: [
        { size: 50000, profitTarget: 3000, maxDrawdown: 2000, dailyLossLimit: 1000, activationFee: 0 },
        { size: 100000, profitTarget: 6000, maxDrawdown: 4000, dailyLossLimit: 2000, activationFee: 0 },
        { size: 150000, profitTarget: 9000, maxDrawdown: 6000, dailyLossLimit: 3000, activationFee: 0 },
      ],
    },
  ],
};

// ─── Exports ────────────────────────────────────────────────────────────────

export const firms: PropFirm[] = [
  // Futures
  apex,
  topstep,
  lucid,
  tradeify,
  mff,
  // Crypto
  tradeifyCrypto,
  breakoutProp,
  warbux,
  cryptoFundTrader,
  e8Markets,
];

export function getFirmById(id: string): PropFirm | undefined {
  return firms.find((f) => f.id === id);
}

export function getPlanById(firmId: string, planId: string): PropFirmPlan | undefined {
  const firm = getFirmById(firmId);
  if (!firm) return undefined;
  return firm.plans.find((p) => p.id === planId);
}

export function getSMOGEligibleFirms(): PropFirm[] {
  return firms.filter(isFirmSMOGEligible);
}

export function getFirmsByMarket(market: 'futures' | 'crypto'): PropFirm[] {
  return firms.filter((f) => f.market === market);
}

export function getFirmAccountBySize(firmId: string, planId: string, size: number): PropFirmAccount | undefined {
  const plan = getPlanById(firmId, planId);
  if (!plan) return undefined;
  return plan.accounts.find((a) => a.size === size);
}
